import {blobErrorMessage} from "@/utils/common";

// Query/formData params the catalog documents but the UI must never render
// (03-export-hub-guide.md §3.6): limit/offset are pagination knobs with sane
// backend defaults, lang/currency are locale knobs already covered by the
// app's own language/currency settings. The one exception (initiatives-votes-
// by-board's hidden limit) is injected automatically in buildRequest, never
// shown as a field.
const HIDDEN_PARAM_NAMES = ["limit", "offset", "lang", "currency"];

const TEXT_PARAM_NAMES = [
    "initiative_id",
    "deputy_id",
    "manager_id",
    "search",
    "class_name",
    "title",
    "description",
];

const TRISTATE_BOOLSTR_PARAMS = ["is_partnership", "is_deputy_chosen"];

// Items whose export file language isn't a query param but an `hl` HTTP
// header (documented only in the catalog's `note` text) — special-cased per
// item id since the generic `params` model has no "header" `in` kind.
const HEADER_LANG_ITEM_IDS = ["finance-state-budget"];

const LANG_OPTIONS = [
    {value: "uz_cyr", label: "Ўзбекча (кирилл)"},
    {value: "uz_lat", label: "O'zbekcha (lotin)"},
    {value: "ru", label: "Русский"},
    {value: "en", label: "English"},
    {value: "kr", label: "Qoraqalpoqcha"},
];

function hasLangHeader(item) {
    return HEADER_LANG_ITEM_IDS.includes(item?.id);
}

// Human labels for params whose name isn't already a self-explanatory word —
// falls back to the raw param name when not listed here.
const PARAM_LABELS = {
    board_id: "Mavsum",
    region_id: "Viloyat",
    district_id: "Tuman",
    quarter_id: "Mahalla",
    year: "Yil",
    fin_year: "Yil",
    season: "Mavsum raqami",
    period: "Davr",
    date: "Sana",
    from_date: "Boshlanish sanasi",
    to_date: "Tugash sanasi",
    initiative_id: "Tashabbus ID",
    deputy_id: "Deputat ID",
    manager_id: "Menejer ID",
    search: "Qidiruv",
    class_name: "Sinf nomi",
    title: "Sarlavha",
    description: "Tavsif",
    is_partnership: "Sherikchilik",
    is_deputy_chosen: "Deputat tanlagan",
    is_active: "Holati",
    is_checked: "Tekshirilgan holati",
    send_sms: "SMS yuborish",
};

function toApiPath(path) {
    return `api${path.startsWith("/") ? path : "/" + path}`;
}

function pathParams(item) {
    return (item.params || []).filter((p) => p.in === "path");
}

function bodyParams(item) {
    return (item.params || []).filter((p) => p.in !== "path" && p.type !== "file");
}

function fileParam(item) {
    return (item.params || []).find((p) => p.type === "file");
}

function allFormParams(item) {
    return [...pathParams(item), ...bodyParams(item)];
}

// Params actually rendered as fields in the modal.
function visibleParams(item) {
    return allFormParams(item).filter((p) => !HIDDEN_PARAM_NAMES.includes(p.name));
}

function paramLabel(p) {
    return PARAM_LABELS[p.name] || p.name;
}

// First-match-wins widget resolution — mirrors guide §3.5 render order,
// with the §3.6 name dictionary as step 3.
function resolveWidget(p) {
    if (p.type === "file") return "file";
    if (Array.isArray(p.enum) && p.enum.length) return "enum-select";

    switch (p.name) {
        case "board_id":
            return "board-select";
        case "region_id":
            return "region-select";
        case "district_id":
            return "district-select";
        case "quarter_id":
            return "quarter-select";
        case "year":
        case "fin_year":
            return "year-picker";
        case "season":
            return "number";
        case "period":
            return p.type === "integer" ? "number" : "month-picker";
        case "date": {
            const note = p.note || "";
            if (/YYYY-MM-DD/.test(note)) return "date-picker";
            if (/YYYY-MM/.test(note)) return "month-picker";
            return "text";
        }
        case "from_date":
        case "to_date":
            return "date-picker-rfc3339";
        case "send_sms":
            return "checkbox";
        case "is_active":
            return "tristate-active";
        case "is_checked":
            return "tristate-checked";
        default:
            break;
    }

    if (TRISTATE_BOOLSTR_PARAMS.includes(p.name)) return "tristate-boolstr";
    if (TEXT_PARAM_NAMES.includes(p.name)) return "text";
    if (p.type === "integer") return "number";
    return "text";
}

function isValueFilled(v) {
    return v !== null && v !== undefined && v !== "";
}

// Builds the resolved URL (path params substituted) plus separate query/
// formData maps from the current field values — guide §4/§5, applied
// through axios ($http) rather than raw fetch (see CLAUDE.md: all views call
// the API through this.$http, not window.fetch).
function buildRequest(item, values) {
    let url = item.path;
    const query = {};
    const form = {};

    (item.params || []).forEach((p) => {
        if (p.type === "file") return;
        if (p.in === "path") {
            url = url.replace(`:${p.name}`, encodeURIComponent(values[p.name]));
            return;
        }
        let v = values[p.name];
        if ((p.name === "from_date" || p.name === "to_date") && isValueFilled(v)) {
            v = `${v}T00:00:00Z`;
        }
        if (!isValueFilled(v)) return;
        if (p.in === "formData") form[p.name] = v;
        else query[p.name] = v;
    });

    // §5.2: initiatives-votes-by-board silently returns 0 rows without an
    // explicit limit — never shown as a field, always sent.
    if (item.id === "initiatives-votes-by-board") {
        query.limit = 1000000;
    }

    return {url, query, form};
}

function extractFilename(res, item) {
    try {
        const header = res.headers?.get
            ? res.headers.get("content-disposition")
            : res.headers?.["content-disposition"];
        if (header) {
            const starMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
            if (starMatch) return decodeURIComponent(starMatch[1]);
            const match = header.match(/filename="?([^";]+)"?/i);
            if (match) return match[1];
        }
    } catch (e) {
        // fall through to default name below
    }
    return `${item.id}.xlsx`;
}

function saveBlob(res, item) {
    const blob = new Blob([res.data]);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", extractFilename(res, item));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

async function downloadExport(http, item, values, headers) {
    const {url, query} = buildRequest(item, values);
    const res = await http.get(toApiPath(url), {params: query, responseType: "blob", headers});
    saveBlob(res, item);
}

async function uploadImport(http, item, file, values) {
    const {url, query, form} = buildRequest(item, values);
    const formData = new FormData();
    formData.append("file", file);
    Object.entries(form).forEach(([k, v]) => formData.append(k, v));
    return http.post(toApiPath(url), formData, {
        params: query,
        headers: {"Content-Type": "multipart/form-data"},
    });
}

// §7: uniform error shape. `field` may be PascalCase — compare case/underscore
// -insensitively when matching it back to a param.
function formatInvalidArgs(invalidArgs) {
    if (!Array.isArray(invalidArgs) || !invalidArgs.length) return "";
    const TAG_LABELS = {required: "majburiy"};
    return invalidArgs
        .map((a) => {
            const field = (a.field || a.Field || "").toString();
            const tag = (a.tag || a.Tag || "").toString();
            return tag ? `${field} (${TAG_LABELS[tag] || tag})` : field;
        })
        .filter(Boolean)
        .join(", ");
}

// Export errors arrive as a Blob (responseType: "blob"); import errors arrive
// as plain JSON — both funnel through here so the modal has one error path.
async function extractErrorMessage(error, fallback) {
    let data = error?.response?.data;
    if (data instanceof Blob) {
        const message = await blobErrorMessage(error.response, null);
        try {
            data = JSON.parse(await data.text());
        } catch (e) {
            return message || fallback;
        }
    }
    if (!data) return fallback;
    if (data.message === "See invalid_args" && data.invalid_args) {
        const detail = formatInvalidArgs(data.invalid_args);
        return detail ? `${detail} to'ldirilishi shart` : fallback;
    }
    return data.message || fallback;
}

// §5.1: messages-import responds 200 with a job that's still running.
function isAsyncJobResponse(data) {
    return !!(data && data.job_id && data.status_endpoint);
}

function pollJobStatus(http, statusEndpoint, onSettled, {intervalMs = 4000, maxAttempts = 60} = {}) {
    const path = statusEndpoint.startsWith("/") ? statusEndpoint.slice(1) : statusEndpoint;
    let attempts = 0;
    const TERMINAL = ["done", "completed", "success", "finished", "failed", "error"];
    const timer = setInterval(async () => {
        attempts += 1;
        try {
            const {data} = await http.get(toApiPath(path));
            const status = (data?.status || "").toString().toLowerCase();
            if (TERMINAL.includes(status) || attempts >= maxAttempts) {
                clearInterval(timer);
                onSettled(data);
            }
        } catch (e) {
            clearInterval(timer);
            onSettled(null);
        }
    }, intervalMs);
    return () => clearInterval(timer);
}

export {
    toApiPath,
    pathParams,
    bodyParams,
    fileParam,
    allFormParams,
    visibleParams,
    paramLabel,
    resolveWidget,
    isValueFilled,
    buildRequest,
    downloadExport,
    uploadImport,
    extractErrorMessage,
    isAsyncJobResponse,
    pollJobStatus,
    hasLangHeader,
    LANG_OPTIONS,
};
